import { useEffect, useState } from "react";
import { X, Mail, CheckCircle2, XCircle, Loader2, Eye, EyeOff } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

/**
 * Email audit detail — shows the full metadata of a notification entry
 * plus the rendered HTML body when stored (logged via `_log_attempt`).
 *
 * The HTML is rendered inside an iframe with `sandbox=""` so the email's
 * own scripts / external resources cannot affect the admin app. A "view
 * source" toggle lets the admin inspect the raw HTML if needed.
 */
export default function EmailAuditModal({ notifId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewSource, setViewSource] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/admin/email-audit/${notifId}`)
      .then(({ data }) => { if (alive) setData(data); })
      .catch((e) => { toast.error(formatApiError(e)); onClose?.(); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [notifId, onClose]);

  return (
    <div
      className="fixed inset-0 bg-black/70 z-50 grid place-items-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="email-audit-modal"
    >
      <div
        onClick={e => e.stopPropagation()}
        className="bg-[#062c27] border border-[#1a4640] max-w-3xl w-full p-5 sm:p-6 space-y-4 my-8"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="eyebrow text-[#d4af37] flex items-center gap-2">
              <Mail size={12} /> Email enviado
            </div>
            <h2 className="font-display text-xl sm:text-2xl text-[#fdfbf7] mt-1 truncate">
              {loading ? "A carregar…" : (data?.subject || "—")}
            </h2>
          </div>
          <button onClick={onClose} className="text-[#a0b2af] hover:text-white shrink-0">
            <X size={18} />
          </button>
        </div>

        {loading && (
          <div className="py-10 text-center text-sm text-[#a0b2af] inline-flex items-center gap-2 justify-center w-full">
            <Loader2 size={14} className="animate-spin" /> A carregar detalhes…
          </div>
        )}

        {!loading && data && (
          <>
            {/* Metadata */}
            <section className="border border-[#1a4640] rounded-sm p-4 space-y-2 text-sm">
              <Row label="Estado">
                {data.status === "sent" ? (
                  <span className="inline-flex items-center gap-1 text-[#10b981]">
                    <CheckCircle2 size={13} /> Enviado
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[#fca5a5]">
                    <XCircle size={13} /> Falhado
                  </span>
                )}
              </Row>
              <Row label="Para"><span className="break-all">{data.to}</span></Row>
              <Row label="Tipo"><span className="font-mono text-[#d4af37]">{(data.kind || "").replace("email.", "")}</span></Row>
              <Row label="Quando">{new Date(data.created_at).toLocaleString("pt-PT")}</Row>
              {data.resend_id && (
                <Row label="ID Resend"><span className="font-mono text-xs text-[#a0b2af]">{data.resend_id}</span></Row>
              )}
              {data.error && (
                <div className="border-t border-[#1a4640] mt-3 pt-3">
                  <div className="text-[10px] uppercase tracking-widest text-[#fca5a5] mb-1">Erro</div>
                  <div className="text-[12px] text-[#fecaca] break-words leading-snug">{data.error}</div>
                </div>
              )}
            </section>

            {/* Body */}
            <section className="border border-[#1a4640] rounded-sm overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#1a4640] bg-[#031c18]">
                <div className="eyebrow text-[#d4af37] flex items-center gap-2">
                  <Mail size={12} /> Conteúdo do email
                </div>
                {data.html && (
                  <button
                    onClick={() => setViewSource(s => !s)}
                    className="text-[10px] uppercase tracking-widest text-[#a0b2af] hover:text-[#d4af37] inline-flex items-center gap-1.5"
                    data-testid="email-audit-toggle-source"
                  >
                    {viewSource ? <><EyeOff size={11} /> Pré-visualizar</> : <><Eye size={11} /> Ver código HTML</>}
                  </button>
                )}
              </div>
              {!data.html ? (
                <div className="p-6 text-center text-sm text-[#a0b2af] italic">
                  O conteúdo do email não foi gravado para este envio (envio anterior à introdução desta funcionalidade).
                </div>
              ) : viewSource ? (
                <pre className="p-3 text-[10px] text-[#cde3df] bg-[#031c18] overflow-auto max-h-[460px] whitespace-pre-wrap break-all"
                     data-testid="email-audit-html-source">
                  {data.html}
                </pre>
              ) : (
                <iframe
                  title="Pré-visualização do email"
                  srcDoc={data.html}
                  sandbox=""
                  className="w-full bg-white"
                  style={{ height: 560, border: 0 }}
                  data-testid="email-audit-iframe"
                />
              )}
            </section>

            <div className="flex justify-end">
              <button onClick={onClose}
                      className="px-5 py-2 text-xs uppercase tracking-widest text-[#a0b2af] hover:text-white">
                Fechar
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-start gap-3">
      <div className="text-[10px] uppercase tracking-widest text-[#a0b2af] w-20 shrink-0 mt-0.5">{label}</div>
      <div className="text-[#fdfbf7] flex-1 min-w-0">{children}</div>
    </div>
  );
}
